import { makeAutoObservable } from 'mobx';
import authService from '../services/authService';
import i18n from '../i18n';

class AuthStore {
  user = null;
  accessToken = null;
  refreshToken = null;
  isLoading = false;
  error = null;

  // 2FA state
  requires2FA = false;
  twoFAMethod = null;
  twoFATempToken = null;
  twoFAEmail = null;
  twoFAStatus = null;

  // Impersonation state
  isImpersonating = false;
  originalAdmin = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.initializeAuth();
  }

  // Initialize auth from localStorage
  initializeAuth() {
    const storedUser = localStorage.getItem('user');
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');

    if (storedUser && storedAccessToken) {
      this.user = JSON.parse(storedUser);
      this.accessToken = storedAccessToken;
      this.refreshToken = storedRefreshToken;
    }

    // Check for impersonation state
    const storedOriginalAdmin = localStorage.getItem('originalAdmin');
    if (storedOriginalAdmin) {
      this.isImpersonating = true;
      this.originalAdmin = JSON.parse(storedOriginalAdmin);
    }
  }

  // Computed values
  get isAuthenticated() {
    return !!this.accessToken && !!this.user;
  }

  get isClient() {
    return this.user?.role === 'CLIENT';
  }

  get isPartner() {
    return this.user?.role === 'PARTNER';
  }

  get isAdmin() {
    return this.user?.role === 'ADMIN';
  }

  // Actions
  setUser(user) {
    this.user = user;
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.removeItem('accessToken');
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
  }

  setError(error) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // Login
  async login(email, password) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await authService.login(email, password);

      // Check if 2FA is required
      if (response.requires_2fa) {
        this.requires2FA = true;
        this.twoFAMethod = response.method;
        this.twoFATempToken = response.temp_token;
        this.twoFAEmail = response.email;
        this.setLoading(false);
        return { success: true, requires2FA: true, method: response.method };
      }

      this.setUser(response.user);
      this.setTokens(response.access, response.refresh);
      this.reset2FAState();

      // Sync language preference from user profile
      if (response.user?.preferred_language) {
        i18n.changeLanguage(response.user.preferred_language);
      }

      this.setLoading(false);
      return { success: true };
    } catch (error) {
      // Check if email not verified
      const errorData = error.response?.data;
      if (errorData?.email_not_verified) {
        this.setLoading(false);
        return {
          success: false,
          emailNotVerified: true,
          email: errorData.email,
          error: errorData.message
        };
      }

      const errorMessage = errorData?.detail || 'Login failed. Please check your credentials.';
      this.setError(errorMessage);
      this.setLoading(false);
      return { success: false, error: errorMessage };
    }
  }

  // Verify 2FA during login
  async verify2FALogin(code) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await authService.verify2FALogin(
        this.twoFAEmail,
        code,
        this.twoFATempToken
      );

      this.setUser(response.user);
      this.setTokens(response.access, response.refresh);
      this.reset2FAState();

      // Sync language preference from user profile
      if (response.user?.preferred_language) {
        i18n.changeLanguage(response.user.preferred_language);
      }

      this.setLoading(false);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Invalid verification code.';
      this.setError(errorMessage);
      this.setLoading(false);
      return { success: false, error: errorMessage };
    }
  }

  // Send email code for 2FA
  async send2FAEmailCode() {
    try {
      const response = await authService.send2FAEmailCode(
        this.twoFAEmail,
        this.twoFATempToken
      );
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to send code.' };
    }
  }

  // Reset 2FA state
  reset2FAState() {
    this.requires2FA = false;
    this.twoFAMethod = null;
    this.twoFATempToken = null;
    this.twoFAEmail = null;
  }

  // Cancel 2FA login
  cancel2FALogin() {
    this.reset2FAState();
    this.setError(null);
  }

  // Fetch 2FA status
  async fetch2FAStatus() {
    try {
      const response = await authService.get2FAStatus();
      this.twoFAStatus = response;
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  }

  // Setup 2FA
  async setup2FA(method = 'TOTP') {
    try {
      const response = await authService.setup2FA(method);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  }

  // Verify 2FA setup
  async verify2FASetup(code) {
    try {
      const response = await authService.verify2FASetup(code);
      await this.fetch2FAStatus();
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  }

  // Disable 2FA
  async disable2FA(password) {
    try {
      const response = await authService.disable2FA(password);
      await this.fetch2FAStatus();
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  }

  // Regenerate backup codes
  async regenerateBackupCodes(password) {
    try {
      const response = await authService.regenerateBackupCodes(password);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  }

  // Register
  async register(userData) {
    this.setLoading(true);
    this.setError(null);

    try {
      await authService.register(userData);
      this.setLoading(false);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.email?.[0]
        || error.response?.data?.password?.[0]
        || error.response?.data?.message
        || 'Registration failed. Please try again.';
      this.setError(errorMessage);
      this.setLoading(false);
      return { success: false, error: errorMessage };
    }
  }

  // Impersonate user (switch to another user's account)
  switchToUser(userData, accessToken, refreshToken, originalAdminData) {
    // Store current admin credentials for switching back
    const adminBackup = {
      user: this.user,
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
    };
    localStorage.setItem('originalAdmin', JSON.stringify(adminBackup));

    // Set new user data
    this.setUser(userData);
    this.setTokens(accessToken, refreshToken);
    this.isImpersonating = true;
    this.originalAdmin = adminBackup;

    // Clear stores for new user context
    if (this.rootStore) {
      if (this.rootStore.cartStore?.reset) {
        this.rootStore.cartStore.reset();
      }
      if (this.rootStore.favoriteStore?.reset) {
        this.rootStore.favoriteStore.reset();
      }
      if (this.rootStore.appointmentStore?.reset) {
        this.rootStore.appointmentStore.reset();
      }
      if (this.rootStore.petStore?.reset) {
        this.rootStore.petStore.reset();
      }
    }
  }

  // Switch back to original admin account
  switchBack() {
    if (!this.isImpersonating || !this.originalAdmin) {
      return false;
    }

    // Restore admin credentials
    this.setUser(this.originalAdmin.user);
    this.setTokens(this.originalAdmin.accessToken, this.originalAdmin.refreshToken);

    // Clear impersonation state
    this.isImpersonating = false;
    this.originalAdmin = null;
    localStorage.removeItem('originalAdmin');

    // Clear stores for admin context
    if (this.rootStore) {
      if (this.rootStore.cartStore?.reset) {
        this.rootStore.cartStore.reset();
      }
      if (this.rootStore.favoriteStore?.reset) {
        this.rootStore.favoriteStore.reset();
      }
      if (this.rootStore.appointmentStore?.reset) {
        this.rootStore.appointmentStore.reset();
      }
      if (this.rootStore.petStore?.reset) {
        this.rootStore.petStore.reset();
      }
    }

    return true;
  }

  // Logout
  logout() {
    this.setUser(null);
    this.setTokens(null, null);
    this.setError(null);

    // Clear impersonation state
    this.isImpersonating = false;
    this.originalAdmin = null;
    localStorage.removeItem('originalAdmin');

    // Clear cart and other user-specific data from stores
    if (this.rootStore) {
      if (this.rootStore.cartStore?.reset) {
        this.rootStore.cartStore.reset();
      }
      if (this.rootStore.favoriteStore?.reset) {
        this.rootStore.favoriteStore.reset();
      }
      if (this.rootStore.appointmentStore?.reset) {
        this.rootStore.appointmentStore.reset();
      }
      if (this.rootStore.petStore?.reset) {
        this.rootStore.petStore.reset();
      }
    }
  }

  // Update user profile
  updateUser(updatedUser) {
    this.setUser({ ...this.user, ...updatedUser });
  }

  // Refresh access token
  async refreshAccessToken() {
    if (!this.refreshToken) {
      this.logout();
      return false;
    }

    try {
      const response = await authService.refreshToken(this.refreshToken);
      this.setTokens(response.access, response.refresh);
      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }
}

export default AuthStore;
